import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Activity, Loader2, AlertTriangle, TrendingUp, Users as UsersIcon,
    MessageSquare, RefreshCw, ChevronDown, ChevronRight,
} from 'lucide-react';

/**
 * Admin → Quality Review (A93).
 *
 * Pulls /api/admin/quality-review and surfaces classifier/engine/scribe
 * improvement opportunities for the last N hours. Read-only.
 */
const HOUR_PRESETS = [
    { value: 24,  label: 'Last 24h' },
    { value: 48,  label: 'Last 48h' },
    { value: 168, label: 'Last 7 days' },
    { value: 720, label: 'Last 30 days' },
];

export default function AdminQuality() {
    const [hours, setHours] = useState(48);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});

    const load = async (h = hours) => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/admin/quality-review?hours=${h}`);
            setData(data);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Could not load');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(hours); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hours]);

    return (
        <div className="p-6 lg:p-10 space-y-8" data-testid="admin-quality-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="overline">Quality review</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2 flex items-center gap-3">
                        <Activity size={26} className="text-marigold" /> Engine quality audit
                    </h1>
                    <p className="text-sm text-slate-400 mt-2 max-w-2xl">
                        Aggregates recent questions and surfaces patterns that indicate
                        improvement opportunities — classifier mis-routing, missing
                        windows, low-confidence verdicts, narration fallbacks.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={hours}
                        onChange={(e) => setHours(Number(e.target.value))}
                        className="bg-elevated border border-marigold/20 text-parchment rounded-sm px-3 py-2 text-sm"
                        data-testid="quality-hours-select"
                    >
                        {HOUR_PRESETS.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => load()}
                        disabled={loading}
                        className="bg-marigold/15 hover:bg-marigold/25 text-marigold border border-marigold/30 rounded-sm px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="quality-refresh-btn"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </header>

            {loading && !data && (
                <div className="min-h-[40vh] flex items-center justify-center" data-testid="admin-quality-loading">
                    <Loader2 className="animate-spin text-marigold" size={24} />
                </div>
            )}
            {error && (
                <Card className="bg-red-950/30 border-red-700/50 rounded-sm p-5 text-sm text-red-200"
                      data-testid="admin-quality-error">
                    Could not load quality review: {error}
                </Card>
            )}
            {data && (
                <>
                    {/* Headline metrics */}
                    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quality-headline">
                        <Metric
                            icon={MessageSquare}
                            label="Total questions"
                            value={data.total_questions}
                            testId="quality-metric-total"
                        />
                        <Metric
                            icon={UsersIcon}
                            label="Distinct users"
                            value={data.distinct_users}
                            testId="quality-metric-users"
                        />
                        <Metric
                            icon={AlertTriangle}
                            label="Improvement flags"
                            value={data.improvement_flags.reduce((a, f) => a + f.count, 0)}
                            accent={data.improvement_flags.length > 0}
                            testId="quality-metric-flags"
                        />
                        <Metric
                            icon={TrendingUp}
                            label="Window"
                            value={`${data.hours}h`}
                            testId="quality-metric-window"
                        />
                    </section>

                    {/* Distribution cards */}
                    <section className="grid lg:grid-cols-3 gap-4">
                        <DistributionCard
                            title="Mode"
                            data={data.mode_distribution}
                            testId="quality-mode-dist"
                            highlight={{ horary: 'text-marigold', natal: 'text-violet-300' }}
                        />
                        <DistributionCard
                            title="Verdict"
                            data={data.verdict_distribution}
                            testId="quality-verdict-dist"
                            highlight={{
                                YES: 'text-green-300',
                                NO: 'text-red-300',
                                TRAJECTORY: 'text-violet-300',
                                YES_WITH_DELAY: 'text-amber-300',
                                NOT_CONFIGURED: 'text-slate-500',
                            }}
                        />
                        <DistributionCard
                            title="Top categories"
                            data={data.category_distribution}
                            testId="quality-cat-dist"
                            limit={8}
                        />
                    </section>

                    {/* Improvement flags */}
                    <section data-testid="quality-improvement-flags">
                        <h2 className="font-serif text-xl text-parchment mb-3 flex items-center gap-2">
                            <AlertTriangle size={18} className="text-amber-400" />
                            Improvement opportunities
                        </h2>
                        {data.improvement_flags.length === 0 && (
                            <Card className="bg-elevated border-marigold/15 rounded-sm p-6 text-sm text-slate-400">
                                No improvement flags surfaced in this window. The classifier
                                routed every question cleanly and no engine fallback path
                                was triggered.
                            </Card>
                        )}
                        <div className="space-y-2">
                            {data.improvement_flags.map((flag) => (
                                <FlagBlock
                                    key={flag.type}
                                    flag={flag}
                                    expanded={!!expanded[flag.type]}
                                    onToggle={() => setExpanded((m) => ({ ...m, [flag.type]: !m[flag.type] }))}
                                />
                            ))}
                        </div>
                    </section>

                    {/* Recent questions */}
                    <section data-testid="quality-recent">
                        <h2 className="font-serif text-xl text-parchment mb-3">Recent questions</h2>
                        <Card className="bg-elevated border-marigold/15 rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-surface/80 text-slate-400 uppercase tracking-wider text-[10px]">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-normal">Time</th>
                                            <th className="text-left px-3 py-2 font-normal">User</th>
                                            <th className="text-left px-3 py-2 font-normal">Category</th>
                                            <th className="text-left px-3 py-2 font-normal">Mode</th>
                                            <th className="text-left px-3 py-2 font-normal">Verdict</th>
                                            <th className="text-left px-3 py-2 font-normal">Flags</th>
                                            <th className="text-left px-3 py-2 font-normal">Text</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.recent_questions.map((q) => (
                                            <tr key={q.id} className="border-t border-marigold/5 hover:bg-surface/30">
                                                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                                                    {(q.created_at || '').slice(5, 16).replace('T', ' ')}
                                                </td>
                                                <td className="px-3 py-2 text-slate-300 max-w-[180px] truncate" title={q.user_email}>
                                                    {q.user_email || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-slate-300">{q.category || '—'}</td>
                                                <td className="px-3 py-2">
                                                    <span className={q.mode === 'horary' ? 'text-marigold' : 'text-violet-300'}>
                                                        {q.mode || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-slate-300">{q.verdict || '—'}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-wrap gap-1">
                                                        {q.is_trajectory_question && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-violet-500/40 text-violet-300">traj</Badge>}
                                                        {q.is_compound_question && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-500/40 text-amber-300">cmpd · {q.sub_question_count}</Badge>}
                                                        {q.primary_intent_timing && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-500/40 text-emerald-300">prim-time</Badge>}
                                                        {q.direction === 'past' && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-slate-500/40 text-slate-300">past</Badge>}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-parchment max-w-[420px] truncate" title={q.text}>{q.text}</td>
                                            </tr>
                                        ))}
                                        {data.recent_questions.length === 0 && (
                                            <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No questions in this window.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </section>

                    <div className="text-[10px] text-slate-600 font-mono">
                        since {data.since} · until {data.until}
                    </div>
                </>
            )}
        </div>
    );
}


function Metric({ icon: Icon, label, value, accent, testId }) {
    return (
        <Card className={`bg-elevated border-marigold/15 rounded-sm p-4 ${accent ? 'border-marigold/40' : ''}`}
              data-testid={testId}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon size={14} className={accent ? 'text-marigold' : 'text-slate-400'} />
                <span className="overline text-[10px]">{label}</span>
            </div>
            <div className={`font-serif text-2xl ${accent ? 'text-marigold' : 'text-parchment'}`}>{value}</div>
        </Card>
    );
}


function DistributionCard({ title, data, testId, highlight, limit }) {
    const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
    const shown = limit ? entries.slice(0, limit) : entries;
    const total = entries.reduce((a, [, v]) => a + v, 0) || 1;
    return (
        <Card className="bg-elevated border-marigold/15 rounded-sm p-5" data-testid={testId}>
            <div className="overline mb-3">{title}</div>
            {entries.length === 0 && <div className="text-sm text-slate-500">No data</div>}
            <div className="space-y-2">
                {shown.map(([k, v]) => {
                    const pct = (100 * v / total).toFixed(0);
                    const tone = highlight?.[k] || 'text-parchment';
                    return (
                        <div key={k} className="text-sm">
                            <div className="flex justify-between mb-1">
                                <span className={tone}>{k}</span>
                                <span className="text-slate-400 font-mono tabular-nums">{v} <span className="text-slate-600">({pct}%)</span></span>
                            </div>
                            <div className="h-1 bg-surface/60 rounded-full overflow-hidden">
                                <div className="h-full bg-marigold/40" style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}


function FlagBlock({ flag, expanded, onToggle }) {
    const SeverityTone = flag.count >= 5
        ? 'border-red-700/40 bg-red-950/20'
        : flag.count >= 2
            ? 'border-amber-700/40 bg-amber-950/15'
            : 'border-slate-700/40 bg-surface/20';
    return (
        <Card className={`${SeverityTone} border rounded-sm`} data-testid={`quality-flag-${flag.type}`}>
            <button
                onClick={onToggle}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-marigold/5 transition-colors"
                data-testid={`quality-flag-${flag.type}-toggle`}
            >
                {expanded ? <ChevronDown size={16} className="text-slate-400 mt-1 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 mt-1 shrink-0" />}
                <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="font-mono text-marigold text-sm">{flag.count}</span>
                        <span className="text-slate-500 text-xs">({flag.pct_of_total}%)</span>
                        <span className="font-serif text-lg text-parchment">{prettify(flag.type)}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{flag.engineering_hint}</p>
                </div>
            </button>
            {expanded && flag.sample_questions.length > 0 && (
                <div className="border-t border-marigold/10 p-4 space-y-2" data-testid={`quality-flag-${flag.type}-samples`}>
                    <div className="overline mb-1.5">Sample questions</div>
                    {flag.sample_questions.map((s) => (
                        <div key={s.id} className="text-xs leading-relaxed bg-obsidian/40 border border-marigold/10 rounded-sm p-3">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-marigold font-mono text-[10px]">{(s.created_at || '').slice(5, 16).replace('T',' ')}</span>
                                <span className="text-slate-400">{s.user || '—'}</span>
                                {s.category && <Badge variant="outline" className="text-[10px] py-0 px-1.5">{s.category}</Badge>}
                                {s.mode && <Badge variant="outline" className="text-[10px] py-0 px-1.5">{s.mode}</Badge>}
                                {s.verdict && <Badge variant="outline" className="text-[10px] py-0 px-1.5">{s.verdict}</Badge>}
                                {s.confidence != null && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-500/40 text-amber-300">conf {s.confidence}</Badge>}
                            </div>
                            <div className="text-parchment">{s.text}</div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}


function prettify(t) {
    return (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
